<script setup lang="ts">
/**
 * Sitefooter (landmark: contentinfo).
 *
 * Inhoud komt volledig uit het thema (rich text) — niets wordt hier
 * vastgeprikt. Een beheerder bepaalt zelf wat erin staat, inclusief of er
 * een link naar de toegankelijkheidsverklaring in komt (die pagina blijft
 * hoe dan ook rechtstreeks bereikbaar via /accessibility, ook zonder link
 * vanuit de footer). Zonder eigen inhoud toont de footer niets; met de
 * schakelaar in het thema gaat 'm helemaal uit.
 */
import { computed } from 'vue'
import DOMPurify from 'dompurify'
import { useThemeStore, readableTextColor } from '../../stores/theme'

const theme = useThemeStore()

/**
 * Footer-HTML is al bij het opslaan door de backend gesaneerd; hier gaat het
 * nog een keer door DOMPurify vóór de v-html-binding. Dezelfde
 * defense-in-depth als bij pagina-inhoud (zie PageView).
 */
const safeContent = computed(() =>
  theme.styles.footer.content
    ? DOMPurify.sanitize(theme.styles.footer.content, { USE_PROFILES: { html: true } })
    : ''
)

const backgroundColor = computed(() => theme.styles.footer.backgroundColor)

/**
 * Tekstkleur: een eigen keuze gaat vóór. Is die er niet, dan bepaalt de
 * helderheid van de achtergrond of de tekst wit of bijna-zwart wordt — met de
 * standaard grijze tekst zou een donkere footer onleesbaar zijn. Zonder beide
 * blijft de standaardvormgeving (bg-white / text-gray-500) staan.
 */
const textColor = computed(() => {
  const own = theme.styles.footer.textColor
  if (own) return own
  return backgroundColor.value ? readableTextColor(backgroundColor.value) : null
})

/** Krijgt de footer eigen kleuren (en erft de inhoud dus de tekstkleur)? */
const themed = computed(() => Boolean(backgroundColor.value || textColor.value))

const footerStyle = computed(() => {
  if (!themed.value) return undefined
  const style: Record<string, string> = {}
  if (backgroundColor.value) style.backgroundColor = backgroundColor.value
  if (textColor.value) style.color = textColor.value
  return style
})
</script>

<template>
  <footer
    v-if="theme.styles.footer.enabled && safeContent"
    class="border-t border-gray-200 py-4 px-4 sm:px-6 text-sm"
    :class="[
      themed ? 'themed-footer' : 'text-gray-500',
      // Zonder eigen achtergrondkleur blijft de footer wit — ook als alleen
      // de tekstkleur is ingesteld.
      backgroundColor ? '' : 'bg-white',
    ]"
    :style="footerStyle"
  >
    <div class="prose prose-sm max-w-none" v-html="safeContent"></div>
  </footer>
</template>

<style scoped>
/* Met eigen footerkleuren erft alle inhoud (ook rich text en links) de
   ingestelde/berekende tekstkleur; anders zouden de prose-standaardkleuren
   tegen een donkere achtergrond wegvallen. */
.themed-footer :deep(*) {
  color: inherit;
}
</style>
